import { translate } from '@/utils/translation';
import { inject, InjectionKey, watch, ref, computed } from 'vue';
import { useOSettings, defaultSettings } from './use-settings';

const locale = ref();

/**
 * A Vue composable that watches for changes in the user's locale settings.
 *
 * @function
 * @name watchLocaleChange
 * @description
 * This composable function sets up a watcher on the user's locale settings and
 * triggers a callback whenever the locale changes.
 *
 * @param {function} callback - A function to be called with the new locale string
 * whenever the locale changes.
 * @returns {void}
 */
export function watchLocaleChange(callback: (locale: string) => void) {
  watch(locale, callback);
}

export const fallbackLocaleKey: InjectionKey<string> = Symbol('Helix Fallback Locale');

// TODO: Import specified fallback locale from settings instead
/**
 * A Vue composable that provides locale fallback functionality.
 *
 * @function
 * @name useHLocaleFallback
 * @description
 * This composable retrieves the fallback locale for the application. If no fallback locale is specified, it defaults to 'en'.
 *
 * @returns {string} The fallback locale.
 */
export function useHLocaleFallback() {
  // Default fallback, if not specified, is `en`
  const settings = useOSettings();
  const localKey = inject(fallbackLocaleKey);

  const fallbackLocale = computed(() => localKey ?? settings?.fallback_locale?.value ?? 'en');

  return fallbackLocale;
}

export function useHLocale() {
  function hSetLocale(newLocale: string) {
    locale.value = newLocale;
  }

  return { locale, hSetLocale };
}

/**
 * A Vue composable that provides translation functionalities.
 *
 * @function
 * @name useHTranslate
 * @description
 * This composable provides a method to translate a given key using optional data and a fallback locale.
 * If the translation is not found, it returns the key itself.
 *
 * @param {string} key - The key to be translated.
 * @param {Object} [data] - Optional data to be used in the translation.
 * @returns {string} - The translated string or the key if the translation is not found.
 */

export function useHTranslate(key: string, data?: Object) {
  let fallbackLocale: string;

  try {
    fallbackLocale = useHLocaleFallback().value;
  } catch {
    fallbackLocale = defaultSettings.fallback_locale ?? 'en';
  }

  // Don't use computed here. Will result in translations marked in quotation marks when used in the template
  return translate(key, data, fallbackLocale) || key;
}
